import { Injectable, Injector, NgZone, runInInjectionContext, signal } from '@angular/core';
import { RemoteConfig, fetchAndActivate, getString } from '@angular/fire/remote-config';
import { AvailabilityMode, SystemAvailabilityResponse } from '../models/myapp.model';

type AvailabilityConfig = {
  mode?: string;
  message?: string;
};

const NORMAL_STATUS: SystemAvailabilityResponse = {
  mode: 'NORMAL',
  readsAllowed: true,
  writesAllowed: true,
  message: 'The system is available.',
};

// Use an absolute Firebase Hosting URL so old Capacitor apps fetch the current
// status file instead of the bundled asset inside the installed app.
// const HOSTED_STATUS_URL = 'https://YOUR_FIREBASE_PROJECT.web.app/assets/system-status.json';
// const HOSTED_STATUS_URL = 'assets/system-status.json';
// const LOCAL_STATUS_URL = 'assets/system-status.json';

// in 'Remote Config' at https://console.firebase.google.com/project/REPLACE_WITH/config/env/firebase  create string app_availability_mode as either of OFFLINE  NORMAL READ_ONLY
const AVAILABILITY_MODE_KEY = 'app_availability_mode';
const AVAILABILITY_MESSAGE_KEY = 'app_availability_message';

@Injectable({ providedIn: 'root' })
export class AvailabilityService {
  readonly status = signal<SystemAvailabilityResponse>(NORMAL_STATUS);
  readonly isReady = signal(false);

  private initializePromise?: Promise<void>;

  constructor(private remoteConfig: RemoteConfig, private ngZone: NgZone, private injector: Injector) {}

  initialize(): Promise<void> {
    this.initializePromise ??= this.runInitialize();
    return this.initializePromise;
  }

  mode(): AvailabilityMode {
    return this.status().mode;
  }

  message(): string {
    return this.status().message;
  }

  private async runInitialize(): Promise<void> {
    await this.refresh();
    this.ngZone.run(() => this.isReady.set(true));

    setInterval(() => {
      void this.refresh();
    }, 600000); // 10 minutes
  }

  private async refresh(): Promise<void> {
    const configStatus = await this.readConfig();
    // console.log(JSON.stringify(configStatus, null, 2), ' and message is.  ', configStatus?.message);
    this.ngZone.run(() => this.status.set(configStatus ?? NORMAL_STATUS));
  }

  private async readConfig(): Promise<SystemAvailabilityResponse | null> {
    try {
      await runInInjectionContext(this.injector, () => fetchAndActivate(this.remoteConfig)); 
      return runInInjectionContext(this.injector, () =>
        this.normalize({
          mode: getString(this.remoteConfig, AVAILABILITY_MODE_KEY), 
          message: getString(this.remoteConfig, AVAILABILITY_MESSAGE_KEY), 
        }),
      );
    } catch {
      return null;
    }
  }

  private normalize(value: AvailabilityConfig): SystemAvailabilityResponse {
    const mode = this.normalizeMode(value.mode);
    const message = value.message?.trim();
    return {
      mode,
      readsAllowed: mode !== 'OFFLINE',
      writesAllowed: mode === 'NORMAL',
      message: message || this.messageFor(mode),
      checkedAt: new Date().toISOString(),
    };
  }

  private normalizeMode(value?: string): AvailabilityMode {
    const mode = value?.trim().toUpperCase();
    if (mode === 'READ_ONLY' || mode === 'OFFLINE' || mode === 'NORMAL') {
      return mode;
    }
    return 'NORMAL';
  }

  private messageFor(mode: AvailabilityMode): string {
    if (mode === 'READ_ONLY') {
      return 'System maintenance is in progress. Viewing is available, but saving changes is temporarily disabled.';
    }
    if (mode === 'OFFLINE') {
      return 'The system is temporarily unavailable while maintenance is in progress.';
    }
    return 'The system is available.';
  }
}
